<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import IntegrationsAPI from 'dashboard/api/integrations';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

// Replaces manually typing a Cal.com Event Type ID (the source of a real
// booking bug — a booking-page URL was pasted in instead of a numeric id)
// with a dropdown fetched live from the connected account, so only real,
// selectable event types can ever be configured.
const { t } = useI18n();
const store = useStore();

const integrationGetter = useMapGetter('integrations/getIntegration');
const integration = computed(() => integrationGetter.value('cal_com'));
const currentEventTypeId = computed(
  () => integration.value?.settings?.event_type_id || ''
);

const isLoading = ref(false);
const isSaving = ref(false);
const loadError = ref('');
const eventTypes = ref([]);

const eventTypeOptions = computed(() =>
  eventTypes.value.map(eventType => ({
    label: `${eventType.title} (${eventType.length_in_minutes || eventType.lengthInMinutes} min)`,
    value: String(eventType.id),
  }))
);
const selectedEventTypeId = ref('');

const fetchEventTypes = async () => {
  isLoading.value = true;
  loadError.value = '';
  try {
    const response = await IntegrationsAPI.getCalComEventTypes();
    eventTypes.value = response.data.payload;
    selectedEventTypeId.value = currentEventTypeId.value;
  } catch (error) {
    loadError.value =
      error?.response?.data?.error ||
      t('CALENDAR_INTEGRATIONS.EVENT_TYPE.FETCH_ERROR');
  } finally {
    isLoading.value = false;
  }
};

const handleSelect = async eventTypeId => {
  selectedEventTypeId.value = eventTypeId;
  isSaving.value = true;
  try {
    await store.dispatch('integrations/updateCalComEventType', eventTypeId);
    useAlert(t('CALENDAR_INTEGRATIONS.EVENT_TYPE.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('CALENDAR_INTEGRATIONS.EVENT_TYPE.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(fetchEventTypes);
</script>

<template>
  <div
    class="flex flex-col gap-2 p-4 mt-2 border rounded-xl border-n-weak bg-n-solid-2"
  >
    <h4 class="mb-0 text-sm font-medium text-n-slate-12">
      {{ t('CALENDAR_INTEGRATIONS.EVENT_TYPE.TITLE') }}
    </h4>
    <p class="mt-0 mb-1 text-xs text-n-slate-11">
      {{ t('CALENDAR_INTEGRATIONS.EVENT_TYPE.DESCRIPTION') }}
    </p>

    <div
      v-if="isLoading"
      class="flex items-center gap-2 text-sm text-n-slate-11"
    >
      <Spinner />
      {{ t('CALENDAR_INTEGRATIONS.EVENT_TYPE.LOADING') }}
    </div>

    <div v-else-if="loadError" class="flex items-center gap-2">
      <p class="m-0 text-sm text-n-ruby-11">{{ loadError }}</p>
      <Button
        :label="t('CALENDAR_INTEGRATIONS.EVENT_TYPE.RETRY')"
        size="xs"
        variant="faded"
        color="slate"
        @click="fetchEventTypes"
      />
    </div>

    <p v-else-if="!eventTypeOptions.length" class="m-0 text-sm text-n-slate-11">
      {{ t('CALENDAR_INTEGRATIONS.EVENT_TYPE.EMPTY') }}
    </p>

    <ComboBox
      v-else
      :model-value="selectedEventTypeId"
      :options="eventTypeOptions"
      :placeholder="t('CALENDAR_INTEGRATIONS.EVENT_TYPE.PLACEHOLDER')"
      :disabled="isSaving"
      @update:model-value="handleSelect"
    />
  </div>
</template>
